/**
 * Demo Admin Page
 *
 * Admin interface for managing demo data configuration, generation, and purge operations.
 * Restricted to APP_ADMIN users only.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PlaylistPlay as PlaylistPlayIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import { API } from 'aws-amplify';

// Layout
import DashboardLayout from 'layouts/DashboardLayout';
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';

// Components
import ClientSelector from 'components/common/admin/ClientSelector';

// Tab Components
import ConfigurationTab from './tabs/ConfigurationTab';
import JobStatusTab from './tabs/JobStatusTab';
import DataViewTab from './tabs/DataViewTab';

// Hooks and queries
import { useUser } from 'hooks/useUser';
import { useAdminContext } from 'context/AdminContext';
import { CHECK_DEMO_BASELINE, SEED_DEMO_CONFIG, START_DEMO_RUN, PURGE_DEMO_DATA, LIST_DEMO_RUNS } from './queries';
import { SCOPE_TYPES, STATUS_COLORS, POLL_INTERVAL, DEFAULT_NOISE_SIGMA, DEFAULT_BASE_SEED, SEASONALITY_PROFILES, WEEKDAY_PROFILES } from './constants';

const DemoAdmin = () => {
  const { user } = useUser();

  // Check if user is APP_ADMIN
  const isAppAdmin = user?.groupRole === 'APP_ADMIN';

  // Use AdminContext for client selection
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin: isAppAdminContext,
    availableClients,
    loading: clientsLoading,
    error: clientsError
  } = useAdminContext();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // State for config seeding
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedMessage, setSeedMessage] = useState(null);
  const [configExists, setConfigExists] = useState(null); // null = not checked, true/false = checked
  const [configCheckLoading, setConfigCheckLoading] = useState(false);
  const [baselineDefaults, setBaselineDefaults] = useState(null); // Stores loaded baseline config

  // State for data generation
  const [genStartDate, setGenStartDate] = useState('');
  const [genEndDate, setGenEndDate] = useState('');
  const [genSeasonalityKey, setGenSeasonalityKey] = useState('summer_peak'); // Default from baseline
  const [genWeekdayProfileKey, setGenWeekdayProfileKey] = useState('std_casual'); // Default from baseline
  const [genBaseSeed, setGenBaseSeed] = useState(DEFAULT_BASE_SEED);
  const [genNoiseSigma, setGenNoiseSigma] = useState(DEFAULT_NOISE_SIGMA);
  const [genLoading, setGenLoading] = useState(false);
  const [genMessage, setGenMessage] = useState(null);

  // State for purge
  const [purgeScopeType, setPurgeScopeType] = useState('CLIENT');
  const [purgeScopeId, setPurgeScopeId] = useState('');
  const [purgeStartDate, setPurgeStartDate] = useState('');
  const [purgeEndDate, setPurgeEndDate] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeMessage, setPurgeMessage] = useState(null);

  // State for job status
  const [recentRuns, setRecentRuns] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);

  // Poll for job status
  useEffect(() => {
    if (!selectedClient) return;

    const pollStatus = async () => {
      try {
        setStatusLoading(true);
        const result = await API.graphql({
          query: LIST_DEMO_RUNS,
          variables: { clientId: selectedClient, limit: 5 }
        });

        const items = result.data.listDemoRuns.items || [];
        setRecentRuns(items);

        // Clear activeRunId if the run is no longer active
        if (activeRunId) {
          const activeRun = items.find(run => run.runId === activeRunId);
          if (activeRun && activeRun.status !== 'QUEUED' && activeRun.status !== 'RUNNING') {
            setActiveRunId(null);
          }
        }
      } catch (error) {
        console.error('Error fetching runs:', error);
      } finally {
        setStatusLoading(false);
      }
    };

    // Poll immediately
    pollStatus();

    // Set up interval only if there's an active run
    let interval;
    if (activeRunId) {
      interval = setInterval(pollStatus, POLL_INTERVAL);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedClient, activeRunId]);

  // Check if baseline config exists when client changes and load defaults
  useEffect(() => {
    const checkBaseline = async () => {
      if (!selectedClient) {
        setConfigExists(null);
        setBaselineDefaults(null);
        return;
      }

      try {
        setConfigCheckLoading(true);
        const result = await API.graphql({
          query: CHECK_DEMO_BASELINE,
          variables: { clientId: selectedClient }
        });

        const baseline = result.data.getDemoBaseline;
        if (baseline) {
          setConfigExists(true);
          setBaselineDefaults(baseline);

          // Initialize UI state with baseline defaults
          setGenSeasonalityKey(baseline.defaultSeasonalityKey || 'summer_peak');
          setGenWeekdayProfileKey(baseline.defaultWeekdayProfileKey || 'std_casual');
          setGenBaseSeed(baseline.baseSeed || DEFAULT_BASE_SEED);
          setGenNoiseSigma(baseline.noiseSigma || DEFAULT_NOISE_SIGMA);
        } else {
          setConfigExists(false);
          setBaselineDefaults(null);
        }
      } catch (error) {
        // If error is "Not Found" or similar, config doesn't exist
        console.log('Baseline check:', error);
        setConfigExists(false);
        setBaselineDefaults(null);
      } finally {
        setConfigCheckLoading(false);
      }
    };

    checkBaseline();
  }, [selectedClient]);

  // Handler: Seed Config
  const handleSeedConfig = async () => {
    if (!selectedClient) {
      setSeedMessage({ type: 'error', text: 'Please select a client first' });
      return;
    }

    try {
      setSeedLoading(true);
      setSeedMessage(null);

      const result = await API.graphql({
        query: SEED_DEMO_CONFIG,
        variables: { clientId: selectedClient }
      });

      if (result.data.seedDemoConfig) {
        setSeedMessage({ type: 'success', text: 'Configuration seeded successfully!' });
        setConfigExists(true); // Update status immediately
      } else {
        setSeedMessage({ type: 'info', text: 'Configuration already exists for this client' });
      }
    } catch (error) {
      console.error('Error seeding config:', error);
      setSeedMessage({ type: 'error', text: error.message || 'Failed to seed configuration' });
    } finally {
      setSeedLoading(false);
    }
  };

  // Handler: Start Generation
  const handleStartGeneration = async () => {
    if (!selectedClient || !genStartDate || !genEndDate) {
      setGenMessage({ type: 'error', text: 'Please select a client and enter start/end dates' });
      return;
    }

    try {
      setGenLoading(true);
      setGenMessage(null);

      const result = await API.graphql({
        query: START_DEMO_RUN,
        variables: {
          input: {
            clientId: selectedClient,
            operation: 'GENERATE',
            startDate: genStartDate,
            endDate: genEndDate,
            seasonalityKey: genSeasonalityKey,
            weekdayProfileKey: genWeekdayProfileKey,
            baseSeed: parseInt(genBaseSeed),
            noiseSigma: parseFloat(genNoiseSigma)
          }
        }
      });

      const run = result.data.startDemoRun;
      setActiveRunId(run.runId);
      setGenMessage({
        type: 'success',
        text: `Generation started! Run ID: ${run.runId}`
      });
    } catch (error) {
      console.error('Error starting generation:', error);
      setGenMessage({ type: 'error', text: error.message || 'Failed to start generation' });
    } finally {
      setGenLoading(false);
    }
  };

  // Handler: Purge Data
  const handlePurgeData = async () => {
    if (!selectedClient || !purgeStartDate || !purgeEndDate) {
      setPurgeMessage({ type: 'error', text: 'Please select a client and enter start/end dates' });
      return;
    }

    if ((purgeScopeType === 'ORGUNIT' || purgeScopeType === 'LOCATION') && !purgeScopeId) {
      setPurgeMessage({ type: 'error', text: 'Scope ID is required for ORGUNIT and LOCATION scopes' });
      return;
    }

    // Confirmation
    const confirmed = window.confirm(
      `Are you sure you want to purge demo data for ${purgeScopeType}?\n` +
      `Client: ${selectedClient}\n` +
      `This will delete all atomic records from ${purgeStartDate} to ${purgeEndDate}.`
    );

    if (!confirmed) return;

    try {
      setPurgeLoading(true);
      setPurgeMessage(null);

      const result = await API.graphql({
        query: PURGE_DEMO_DATA,
        variables: {
          scope: {
            clientId: selectedClient,
            scopeType: purgeScopeType,
            scopeId: purgeScopeId || null,
            startDate: purgeStartDate,
            endDate: purgeEndDate
          }
        }
      });

      const stats = result.data.purgeDemoData;
      setPurgeMessage({
        type: 'success',
        text: `Purge complete! Deleted ${stats.atomic} atomic records`
      });
    } catch (error) {
      console.error('Error purging data:', error);
      setPurgeMessage({ type: 'error', text: error.message || 'Failed to purge data' });
    } finally {
      setPurgeLoading(false);
    }
  };

  // Access denied for non-APP_ADMIN users
  if (!isAppAdmin) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access Denied. This page is only accessible to APP_ADMIN users.
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        {/* Page Header */}
        <Typography variant="h4" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
          Demo Data Administration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage demo data configuration, generation, and purge operations
        </Typography>

        {/* Client Selector */}
        {isAppAdmin && (
          <Box sx={{ mb: 3 }}>
            <ClientSelector
              value={selectedClient}
              onChange={setSelectedClient}
              clients={availableClients}
              loading={clientsLoading}
              error={clientsError}
            />
          </Box>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: '#7C3AED',
                  color: 'white !important',
                  borderRadius: '8px 8px 0 0',
                  '& .MuiSvgIcon-root': {
                    color: 'white !important'
                  }
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#7C3AED',
                height: '3px'
              }
            }}
          >
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Data Management"
            />
            <Tab
              icon={<PlaylistPlayIcon />}
              iconPosition="start"
              label="Job Status"
            />
            <Tab
              icon={<TableChartIcon />}
              iconPosition="start"
              label="Data View"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box>
          {tabValue === 0 && (
            <ConfigurationTab
              configCheckLoading={configCheckLoading}
              configExists={configExists}
              handleSeedConfig={handleSeedConfig}
              seedLoading={seedLoading}
              seedMessage={seedMessage}
              genStartDate={genStartDate}
              setGenStartDate={setGenStartDate}
              genEndDate={genEndDate}
              setGenEndDate={setGenEndDate}
              genSeasonalityKey={genSeasonalityKey}
              setGenSeasonalityKey={setGenSeasonalityKey}
              genWeekdayProfileKey={genWeekdayProfileKey}
              setGenWeekdayProfileKey={setGenWeekdayProfileKey}
              genBaseSeed={genBaseSeed}
              setGenBaseSeed={setGenBaseSeed}
              genNoiseSigma={genNoiseSigma}
              setGenNoiseSigma={setGenNoiseSigma}
              handleStartGeneration={handleStartGeneration}
              genLoading={genLoading}
              genMessage={genMessage}
              purgeScopeType={purgeScopeType}
              setPurgeScopeType={setPurgeScopeType}
              purgeScopeId={purgeScopeId}
              setPurgeScopeId={setPurgeScopeId}
              purgeStartDate={purgeStartDate}
              setPurgeStartDate={setPurgeStartDate}
              purgeEndDate={purgeEndDate}
              setPurgeEndDate={setPurgeEndDate}
              handlePurgeData={handlePurgeData}
              purgeLoading={purgeLoading}
              purgeMessage={purgeMessage}
            />
          )}
          {tabValue === 1 && (
            <JobStatusTab
              recentRuns={recentRuns}
              statusLoading={statusLoading}
            />
          )}
          {tabValue === 2 && <DataViewTab selectedClient={selectedClient} />}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default DemoAdmin;
